// frontend/src/layouts/DashboardLayout.jsx
// ======================================================
// ✅ DashboardLayout.jsx – Evalúa Salud IPS
// Autor: Breyner Javier Montalvo Peñarredonda
// ------------------------------------------------------
// Layout institucional principal con Navbar + Sidebar.
// Sincroniza colapso, responsividad y transiciones suaves.
// Implementa mejores prácticas de accesibilidad y UX.
// ======================================================

import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import "../styles/DashboardLayout.css";

const MOBILE_BREAKPOINT = 768;

const DashboardLayout = memo(() => {
  // ======================================================
  // 📱 ESTADOS DE INTERFAZ
  // ======================================================
  const [colapsado, setColapsado] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);

  // ======================================================
  // 🔄 RESPONSIVIDAD: Detectar cambios de tamaño
  // ======================================================
  const handleResize = useCallback(() => {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    if (isMobile) {
      setColapsado(false); // evita sidebar colapsado en móvil
    } else {
      setMenuAbierto(false); // cierra menú lateral cuando vuelve a escritorio
    }
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  // ======================================================
  // 🧭 TOGGLE DE SIDEBAR / MENU MÓVIL
  // ======================================================
  const onToggleMenu = useCallback(() => {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    if (isMobile) {
      setMenuAbierto((prev) => !prev);
    } else {
      setColapsado((prev) => !prev);
    }
  }, []);

  const cerrarMenu = useCallback(() => setMenuAbierto(false), []);

  // ======================================================
  // 🎨 CLASES COMPUTADAS DEL LAYOUT
  // ======================================================
  const layoutClass = useMemo(
    () => `layout${colapsado ? " colapsado" : ""}`,
    [colapsado]
  );

  // ======================================================
  // 🧩 RENDERIZADO PRINCIPAL
  // ======================================================
  return (
    <div className={layoutClass}>
      {/* 🔹 Sidebar institucional */}
      <Sidebar colapsado={colapsado} abierta={menuAbierto} onClose={cerrarMenu} />

      {/* 🔹 Contenido principal */}
      <section className="contenido-principal">
        <Navbar onToggleSidebar={onToggleMenu} />

        <main
          className="main-content fade-in"
          id="main-content"
          role="main"
          aria-live="polite"
        >
          {/* ♿ Enlace accesible para lectores de pantalla */}
          <a href="#main-content" className="skip-link">
            Saltar al contenido principal
          </a>

          {/* 🔻 Contenido dinámico renderizado por las rutas */}
          <Outlet />
        </main>
      </section>
    </div>
  );
});

DashboardLayout.displayName = "DashboardLayout";

export default DashboardLayout;
